import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, MessageCircle, Mic, SlidersHorizontal, Type } from "lucide-react";
import { S, SERIF, SANS, IMG, Eyebrow, Reveal, H2, Section, GoldButton, GhostButton, Photo } from "./slUi";
import { useSpeak, SpeakButton, SpokenLines, Portrait } from "./slSpeak";
import FoundingLightPricing from "../Soulumo/FoundingLightPricing";

const FOUNDATIONS = [["Morning Light", "Begin the day in the state you want"], ["Peace", "Settle a busy mind"], ["Self-Belief", "Strengthen how you see yourself"], ["Rest", "Let the body soften"], ["A Moment for Me", "A few minutes that are only yours"], ["Night", "Slow, spacious, into sleep"]];
const STEPS = [[MessageCircle, "Tell us what's going on."], [Type, "Choose the words that feel right."], [Mic, "Record your voice, or create your voice clone."], [SlidersHorizontal, "Choose the feeling and create your installation."]];

export const Foundations = () => {
  const nav = useNavigate();
  return (
    <Section tone="light" testId="sl-foundations" className="py-24 sm:py-32">
      <div className="max-w-6xl mx-auto">
        <Reveal><Eyebrow>Foundations</Eyebrow></Reveal>
        <div className="mt-4 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal delay={0.05}><H2>Not ready to create yet? <span className="" style={{ color: S.bronze }}>Begin with a Foundation.</span></H2>
            <p className="mt-4 text-base sm:text-lg" style={{ fontFamily: SANS, color: S.inkDim }}>Sometimes you don't need to create anything. You simply need somewhere to begin.</p></Reveal>
          <Reveal delay={0.1}><Photo src={IMG.morning} alt="Beginning the day by the window" ratio="aspect-[16/10]" testId="sl-foundations-photo" /></Reveal>
        </div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {FOUNDATIONS.map(([t, d], i) => (
            <Reveal key={t} delay={0.05 * i}><button type="button" onClick={() => nav("/foundations")} data-testid={`sl-foundation-${i}`}
              className="w-full h-full text-left rounded-2xl p-5 sm:p-7 transition-[transform,background-color] duration-300 hover:-translate-y-1" style={{ background: i % 2 ? S.sand : S.ink, color: i % 2 ? S.ink : S.cream }}>
              <p className="text-2xl sm:text-3xl" style={{ fontFamily: SERIF }}>{t}</p>
              <p className="mt-2 text-xs sm:text-sm" style={{ fontFamily: SANS, opacity: 0.7 }}>{d}</p>
            </button></Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
};

export const Steps = () => (
  <Section tone="sand" testId="sl-steps" className="py-24 sm:py-32">
    <div className="max-w-5xl mx-auto">
      <Reveal><Eyebrow>Creating your own</Eyebrow></Reveal>
      <Reveal delay={0.05}><H2 className="mt-4">Sophisticated underneath. <span className="" style={{ color: S.bronze }}>Simple for you.</span></H2></Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map(([Icon, t], i) => (
          <Reveal key={t} delay={0.1 * i}><div className="h-full rounded-2xl p-6" style={{ background: S.ivory }} data-testid={`sl-step-${i}`}>
            <p className="text-5xl font-light" style={{ fontFamily: SERIF, color: S.bronze }}>{i + 1}</p>
            <Icon size={18} className="mt-4" color={S.bronze} />
            <p className="mt-3 text-xl leading-snug" style={{ fontFamily: SERIF }}>{t}</p>
          </div></Reveal>
        ))}
      </div>
      <Reveal delay={0.3}><p className="mt-8 text-2xl " style={{ fontFamily: SERIF }}>That's it. Adelaida is with you at every step.</p></Reveal>
    </div>
  </Section>
);

export const Price = () => {
  const nav = useNavigate();
  const authed = !!localStorage.getItem("reclaim_token");
  return (
    <Section tone="dark" id="sl-founding" testId="sl-price" className="py-24 sm:py-32">
      <div className="max-w-xl mx-auto text-center">
        <Reveal><Eyebrow light>Be one of the first</Eyebrow></Reveal>
        <Reveal delay={0.05}><H2 className="mt-4">Become a <span className="" style={{ color: S.gold }}>Soulumo Founding member</span></H2></Reveal>
        <Reveal delay={0.1}><p className="mt-5 text-lg " style={{ fontFamily: SERIF, color: S.dim }}>Founding members were here at the beginning. Your price stays at £6.99 a month for as long as you stay.</p></Reveal>
        <div className="mt-10 text-left"><FoundingLightPricing isAuthed={authed} onClaim={() => nav(authed ? "/voice-clone" : "/soulumo/join?next=%2Fvoice-clone")} /></div>
      </div>
    </Section>
  );
};

export const Close = ({ g }) => {
  const nav = useNavigate();
  const s = useSpeak("close");
  return (
    <Section tone="light" testId="sl-close" className="py-20 sm:py-32">
      <div className="max-w-5xl mx-auto">
        <Reveal><Photo src={IMG.open} alt="Open, peaceful, by the sea" ratio="aspect-[4/3] sm:aspect-[21/9]" pos="center 35%" className="mb-14" testId="sl-close-photo" /></Reveal>
        <div className="max-w-3xl">
        <Reveal><p className="text-base sm:text-lg leading-relaxed" style={{ fontFamily: SANS, color: S.inkDim }}>Soulumo isn't asking you to spend hours working on yourself. It isn't another wellbeing programme to complete, or a library of positive statements. It's a way to become more deliberate about one of the most influential conversations in your life.</p></Reveal>
        <div className="mt-10 flex items-center gap-4"><Portrait size={56} speaking={s.state === "playing"} /><p className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ fontFamily: SANS, color: S.bronze }}>Adelaida · Your Soulumo guide</p></div>
        <div className="mt-6"><SpokenLines lines={g.close_lines} s={s} light /></div>
        <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3">
          <GoldButton onClick={() => nav("/soulumo/demo?from=launch")} testId="sl-close-experience">Experience Soulumo <ArrowRight size={14} /></GoldButton>
          <SpeakButton s={s} label="Hear Adelaida" testId="sl-close-adelaida" dark />
          <GhostButton onClick={() => document.getElementById("sl-founding")?.scrollIntoView({ behavior: "smooth" })} testId="sl-close-founding" dark>Founding member · £6.99</GhostButton>
        </div>
        </div>
      </div>
    </Section>
  );
};
